import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  FileText, Plus, Trash2, Edit, AlertTriangle, Clock, CheckCircle, 
  Building2, Calendar, Hash, X, PlusCircle, Paperclip, Download, Upload
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { SendReportButton } from '../components/common/SendReportButton';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [formData, setFormData] = useState({
    document_type: '',
    company_name: '',
    document_number: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [typeDeleteConfirmOpen, setTypeDeleteConfirmOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer';

  useEffect(() => {
    fetchDocuments();
    fetchDocumentTypes();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company-documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company-documents/types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentTypes(data);
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.document_type || !formData.company_name || !formData.expiry_date) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const url = editingDocument 
        ? `${API_URL}/api/company-documents/${editingDocument.id}`
        : `${API_URL}/api/company-documents`;
      
      const response = await fetch(url, {
        method: editingDocument ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchDocuments();
        resetForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error saving document');
      }
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Error saving document');
    }
  };

  const handleDelete = async (id) => {
    const doc = documents.find(d => d.id === id);
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    setDeleting(true);

    try {
      const response = await fetch(`${API_URL}/api/company-documents/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleting(false);
      setDocumentToDelete(null);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      alert('Please enter a document type name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/company-documents/types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newTypeName.trim() })
      });

      if (response.ok) {
        fetchDocumentTypes();
        setNewTypeName('');
        setTypeDialogOpen(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Error adding document type');
      }
    } catch (error) {
      console.error('Error adding document type:', error);
    }
  };

  const handleDeleteType = async (typeId) => {
    const type = documentTypes.find(t => t.id === typeId);
    setTypeToDelete(type);
    setTypeDeleteConfirmOpen(true);
  };

  const confirmDeleteType = async () => {
    if (!typeToDelete) return;
    setDeleting(true);

    try {
      const response = await fetch(`${API_URL}/api/company-documents/types/${typeToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDocumentTypes();
      } else {
        const error = await response.json();
        alert(error.detail || 'Cannot delete this type');
      }
    } catch (error) {
      console.error('Error deleting document type:', error);
    } finally {
      setDeleting(false);
      setTypeToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      document_type: '',
      company_name: '',
      document_number: '',
      issue_date: '',
      expiry_date: '',
      notes: ''
    });
    setEditingDocument(null);
    setDialogOpen(false);
  };

  const openEditDialog = (doc) => {
    setEditingDocument(doc);
    setFormData({
      document_type: doc.document_type,
      company_name: doc.company_name,
      document_number: doc.document_number || '',
      issue_date: doc.issue_date || '',
      expiry_date: doc.expiry_date || '',
      notes: doc.notes || ''
    });
    setDialogOpen(true);
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', days: null };
    
    try {
      const expiry = parseISO(expiryDate);
      const today = new Date();
      const days = differenceInDays(expiry, today);
      
      if (days < 0) return { status: 'expired', days: Math.abs(days) };
      if (days <= 45) return { status: 'expiring', days };
      return { status: 'valid', days };
    } catch {
      return { status: 'unknown', days: null };
    }
  };

  const getStatusBadge = (expiryDate) => {
    const { status, days } = getExpiryStatus(expiryDate);
    
    switch (status) {
      case 'expired':
        return <Badge className="bg-red-500">Expired {days}d ago</Badge>;
      case 'expiring':
        return <Badge className="bg-amber-500">Expires in {days}d</Badge>;
      case 'valid':
        return <Badge className="bg-green-500">Valid</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  // Group documents by status
  const expiredDocs = documents.filter(d => getExpiryStatus(d.expiry_date).status === 'expired');
  const expiringDocs = documents.filter(d => getExpiryStatus(d.expiry_date).status === 'expiring');
  const validDocs = documents.filter(d => getExpiryStatus(d.expiry_date).status === 'valid');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Company Documents</h1>
          <p className="text-gray-500">Manage and track company document expiry dates</p>
        </div>
        {!isViewer && (
          <div className="flex flex-wrap gap-2">
            <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="manage-types-btn">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Document Type
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manage Document Types</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="New document type name..."
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      data-testid="new-type-input"
                    />
                    <Button onClick={handleAddType} data-testid="add-type-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {documentTypes.map((type) => (
                      <div key={type.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{type.name}</span>
                          {type.is_default && (
                            <Badge variant="secondary" className="ml-2 text-xs">Default</Badge>
                          )}
                        </div>
                        {!type.is_default && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteType(type.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-document-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Document
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingDocument ? 'Edit Document' : 'Add New Document'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type *</Label>
                    <Select
                      value={documentTypes.some(t => t.name === formData.document_type) ? formData.document_type : '__custom__'}
                      onValueChange={(value) => {
                        if (value === '__custom__') {
                          setFormData(prev => ({ ...prev, document_type: '' }));
                        } else {
                          setFormData(prev => ({ ...prev, document_type: value }));
                        }
                      }}
                    >
                      <SelectTrigger data-testid="document-type-select">
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map((type) => (
                          <SelectItem key={type.id} value={type.name}>
                            {type.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom__">+ Enter Manually</SelectItem>
                      </SelectContent>
                    </Select>
                    {!documentTypes.some(t => t.name === formData.document_type) && (
                      <Input
                        value={formData.document_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
                        placeholder="Type document name..."
                        className="mt-2"
                        autoFocus
                        data-testid="document-type-manual"
                      />
                    )}
                  </div>
                  
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="e.g., Stars N Bars LLC"
                      data-testid="company-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Document Number / License No.</Label>
                    <Input
                      value={formData.document_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, document_number: e.target.value }))}
                      placeholder="e.g., TL-2024-12345"
                      data-testid="document-number-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Issue Date</Label>
                      <Input
                        type="date"
                        value={formData.issue_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                        data-testid="issue-date-input"
                      />
                    </div>
                    <div>
                      <Label>Expiry Date *</Label>
                      <Input
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                        data-testid="expiry-date-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional notes..."
                      rows={2}
                      data-testid="notes-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-4">
                    <Button variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="save-document-btn">
                      {editingDocument ? 'Update' : 'Save'} Document
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Expired
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{expiredDocs.length}</p>
            <p className="text-sm text-red-600">Documents need immediate attention</p>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              Expiring Soon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-amber-600">{expiringDocs.length}</p>
            <p className="text-sm text-amber-600">Within next 45 days</p>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              Valid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{validDocs.length}</p>
            <p className="text-sm text-green-600">Documents up to date</p>
          </CardContent>
        </Card>
      </div>

      {/* Expired Documents Alert */}
      {expiredDocs.length > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Expired Documents - Immediate Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {expiredDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                  <div className="flex items-center gap-4">
                    <FileText className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="font-medium">{doc.document_type}</p>
                      <p className="text-sm text-gray-600">{doc.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-red-600 font-medium">
                      Expired: {format(parseISO(doc.expiry_date), 'dd MMM yyyy')}
                    </p>
                    {doc.document_number && (
                      <p className="text-xs text-gray-500">#{doc.document_number}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Soon Documents */}
      {expiringDocs.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Expiring Within 45 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {expiringDocs.map((doc) => {
                const { days } = getExpiryStatus(doc.expiry_date);
                return (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                    <div className="flex items-center gap-4">
                      <FileText className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="font-medium">{doc.document_type}</p>
                        <p className="text-sm text-gray-600">{doc.company_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-amber-500">{days} days left</Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        Expires: {format(parseISO(doc.expiry_date), 'dd MMM yyyy')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-600" />
            All Company Documents ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents added yet</p>
              {!isViewer && (
                <Button 
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Document
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold">Document Type</th>
                    <th className="text-left py-3 px-4 font-semibold">Company</th>
                    <th className="text-left py-3 px-4 font-semibold">Document No.</th>
                    <th className="text-left py-3 px-4 font-semibold">Issue Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Expiry Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-left py-3 px-4 font-semibold">Attachments</th>
                    {!isViewer && <th className="text-right py-3 px-4 font-semibold">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-500" />
                          {doc.document_type}
                        </div>
                      </td>
                      <td className="py-3 px-4">{doc.company_name}</td>
                      <td className="py-3 px-4 text-gray-600">{doc.document_number || '-'}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {doc.issue_date ? format(parseISO(doc.issue_date), 'dd MMM yyyy') : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {doc.expiry_date ? format(parseISO(doc.expiry_date), 'dd MMM yyyy') : '-'}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(doc.expiry_date)}</td>
                      <td className="py-3 px-4">
                        <AttachmentCell doc={doc} onUpdate={(updatedDoc) => setDocuments(prev => prev.map(d => d.id === updatedDoc.id ? updatedDoc : d))} isViewer={isViewer} />
                      </td>
                      {!isViewer && (
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(doc)}
                            data-testid={`edit-doc-${doc.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Document Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Company Document"
        description="Are you sure you want to delete this company document record?"
        itemName={documentToDelete ? `${documentToDelete.document_type} - ${documentToDelete.company_name}` : ''}
        onConfirm={confirmDeleteDocument}
        loading={deleting}
      />

      {/* Delete Document Type Confirmation Dialog */}
      <DeleteConfirmDialog
        open={typeDeleteConfirmOpen}
        onOpenChange={setTypeDeleteConfirmOpen}
        title="Delete Document Type"
        description="Are you sure you want to delete this document type? This will not affect existing documents."
        itemName={typeToDelete?.name}
        onConfirm={confirmDeleteType}
        loading={deleting}
      />
    </div>
  );
};


// ── Attachment Cell ──
const AttachmentCell = ({ doc, onUpdate, isViewer }) => {
  const [uploading, setUploading] = useState(false);
  const attachments = doc.attachments || [];
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      alert('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must not exceed 2MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/company-documents/${doc.id}/attachment`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const att = await res.json();
      onUpdate({ ...doc, attachments: [...attachments, att] });
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = (attId, filename) => {
    const url = `${API_URL}/api/company-documents/${doc.id}/attachment/${attId}/download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetch(`${API_URL}/api/company-documents/${doc.id}/attachment/${attId}`, { method: 'DELETE' });
      onUpdate({ ...doc, attachments: attachments.filter(a => a.id !== attId) });
    } catch (err) {
      alert('Delete failed');
    }
  };

  return (
    <div className="space-y-1.5">
      {attachments.map(att => (
        <div key={att.id} className="flex items-center gap-1.5 text-xs group" data-testid={`attachment-${att.id}`}>
          <Paperclip className="h-3 w-3 text-gray-400 flex-shrink-0" />
          <span className="truncate max-w-[100px] text-gray-600" title={att.original_filename}>{att.original_filename}</span>
          <button onClick={() => handleDownload(att.id, att.original_filename)} className="p-0.5 rounded hover:bg-blue-50 text-blue-400" title="Download">
            <Download className="h-3 w-3" />
          </button>
          <SendReportButton
            reportUrl={`/api/company-documents/${doc.id}/attachment/${att.id}/download`}
            filename={att.original_filename}
            defaultSubject={`Document — ${att.original_filename}`}
            iconOnly
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-indigo-400 hover:bg-indigo-50"
            testid={`attach-email-${att.id}`}
          />
          {!isViewer && (
            <button onClick={() => handleDelete(att.id)} className="p-0.5 rounded hover:bg-red-50 text-red-400 opacity-0 group-hover:opacity-100" title="Delete">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {!isViewer && (
        <label className={`inline-flex items-center gap-1 text-xs cursor-pointer ${uploading ? 'text-gray-300' : 'text-purple-500 hover:text-purple-700'}`}>
          <Upload className="h-3 w-3" />
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} disabled={uploading} data-testid={`upload-${doc.id}`} />
        </label>
      )}
    </div>
  );
};

export default CompanyDocuments;
